import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBackNavigation, createBackButton } from '../hooks/useBackNavigation';
import StatusBadge from '../components/StatusBadge';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

function getWdField(wd, key) {
  if (!wd) return undefined;
  if (wd.deal_terms?.[key] !== undefined) return wd.deal_terms[key];
  return wd[key];
}


export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const handleBack = useBackNavigation('deal-detail', '/deals');

  const [deal, setDeal] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [generationLogs, setGenerationLogs] = useState([]);
  const [contractExports, setContractExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState('');
  const [showAllLogs, setShowAllLogs] = useState(false);

  const fetchDealData = useCallback(async () => {
    try {
      setLoading(true);
      const dealRes = await axios.get(`${API_URL}/deals/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setDeal(dealRes.data);

      const merchantRes = await axios.get(`${API_URL}/merchants/${dealRes.data.merchant_id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMerchant(merchantRes.data);

      // Fetch generation logs for this deal
      try {
        const logsRes = await axios.get(`${API_URL}/deals/${id}/generation-logs`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setGenerationLogs(logsRes.data || []);
      } catch (logsErr) {
        console.warn('Failed to load generation logs:', logsErr);
        // Don't fail the whole page if logs can't be loaded
        setGenerationLogs([]);
      }

      // BUG 2 FIX: Fetch contract exports for this deal
      try {
        const exportsRes = await axios.get(`${API_URL}/deals/${id}/contract-exports`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setContractExports(exportsRes.data?.exports || []);
      } catch (exportsErr) {
        console.warn('Failed to load contract exports:', exportsErr);
        // Don't fail the whole page if exports can't be loaded
        setContractExports([]);
      }
    } catch (err) {
      setError('Failed to load deal data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchDealData();
  }, [fetchDealData]);

  // Set up unified header
  useEffect(() => {
    if (!deal || !merchant) return;

    const buttonStyle = {
      padding: `${SPACING.SM} ${SPACING.LG}`,
      backgroundColor: COLORS.BG_SURFACE_PRIMARY,
      color: COLORS.TEXT_PRIMARY,
      border: `1px solid ${COLORS.BORDER_LIGHT}`,
      borderRadius: RADIUS.DEFAULT,
      fontSize: TYPOGRAPHY.BODY.fontSize,
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    };

    const editButtonStyle = {
      padding: `${SPACING.SM} ${SPACING.LG}`,
      backgroundColor: COLORS.PRIMARY,
      color: COLORS.TEXT_PRIMARY,
      border: 'none',
      borderRadius: RADIUS.DEFAULT,
      fontSize: TYPOGRAPHY.BODY.fontSize,
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    };

    setHeaderConfig({
      backButton: createBackButton(handleBack),
      title: '',
      subtitle: '',
      actions: [
        <button key="disclosure" onClick={handleGenerateDisclosure} disabled={generating === 'disclosure'} style={{
          ...buttonStyle,
          opacity: generating === 'disclosure' ? 0.6 : 1,
        }}>
          {generating === 'disclosure' ? 'Generating...' : 'Generate Disclosure'}
        </button>,
        <button key="calculator" onClick={handleGenerateCalculator} disabled={generating === 'calculator'} style={{
          ...buttonStyle,
          opacity: generating === 'calculator' ? 0.6 : 1,
        }}>
          {generating === 'calculator' ? 'Generating...' : 'Generate Calculator'}
        </button>,
        <button key="contract" onClick={() => navigate(`/deals/${id}/contract-output-editor`)} style={buttonStyle}>
          Generate Contract
        </button>,
        <div key="spacer" style={{ width: '1px', backgroundColor: COLORS.BORDER_LIGHT, margin: `0 ${SPACING.SM}` }}></div>,
        <Link key="edit" to={`/deals/${id}/edit`} style={{...editButtonStyle, textDecoration: 'none', display: 'inline-block'}}>
          Edit Deal
        </Link>,
      ],
    });

    // Cleanup: reset header when unmounting
    return () => resetHeaderConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal, merchant, generating, handleBack, setHeaderConfig, resetHeaderConfig, id, navigate]);

  const handleGenerateDisclosure = async () => {
    if (!deal?.id) return;
    try {
      setGenerating('disclosure');
      const res = await axios.post(
        `${API_URL}/deals/${deal.id}/generate-disclosure`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh generation logs to show the new entry
      await fetchDealData();
      // Download the generated file using the proper secured endpoint with auth
      if (res.data.generation_log_id) {
        const downloadRes = await axios.get(
          `${API_URL}/deals/${deal.id}/generations/${res.data.generation_log_id}/file`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        // Create download link and trigger download
        const url = window.URL.createObjectURL(downloadRes.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', downloadRes.headers['content-disposition']?.split('filename=')[1] || 'disclosure.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Failed to generate disclosure');
      console.error(err);
    } finally {
      setGenerating('');
    }
  };

  const handleGenerateCalculator = async () => {
    if (!deal?.id) return;
    try {
      setGenerating('calculator');
      const res = await axios.post(
        `${API_URL}/deals/${deal.id}/generate-calculator`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh generation logs to show the new entry
      await fetchDealData();
      // Download the generated file using the proper secured endpoint with auth
      if (res.data.generation_log_id) {
        const downloadRes = await axios.get(
          `${API_URL}/deals/${deal.id}/generations/${res.data.generation_log_id}/file`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        // Create download link and trigger download
        const url = window.URL.createObjectURL(downloadRes.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', downloadRes.headers['content-disposition']?.split('filename=')[1] || 'calculator.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Failed to generate calculator');
      console.error(err);
    } finally {
      setGenerating('');
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: SPACING.XXXL, color: COLORS.TEXT_LIGHT }}>
          <p style={TYPOGRAPHY.BODY}>Loading advance...</p>
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: SPACING.XXXL, color: COLORS.ERROR }}>
          <p style={TYPOGRAPHY.BODY}>Advance not found</p>
        </div>
      </div>
    );
  }

  const wd = deal.working_data || {};

  const renderDealSummary = (workingData) => {
    return (
      <div style={styles.gridTwoCols}>
        <div style={styles.infoRow}>
          <div style={styles.label}>Status</div>
          <div style={styles.value}>
            <StatusBadge status={deal.status?.toLowerCase() || 'draft'} />
          </div>
        </div>
        <div style={styles.infoRow}>
          <div style={styles.label}>Merchant</div>
          <div style={styles.value}>
            {merchant?.id ? (
              <Link to={`/merchants/${merchant.id}`} style={{ ...styles.entityLink }}>
                {merchant?.legal_name || '—'}
              </Link>
            ) : (
              merchant?.legal_name || '—'
            )}
          </div>
        </div>
        <div style={styles.infoRow}>
          <div style={styles.label}>Advance ID</div>
          <div style={styles.value}>{deal.visual_deal_id || '—'}</div>
        </div>
        <div style={styles.infoRow}>
          <div style={styles.label}>Date Created</div>
          <div style={styles.value}>{new Date(deal.created_at).toLocaleDateString()}</div>
        </div>
        {deal?.funding_entity_id && (
          <div style={styles.infoRow}>
            <div style={styles.label}>Funder</div>
            <div style={styles.value}>
              {deal.funding_entity_id ? (
                <Link to={`/funders/${deal.funding_entity_id}`} style={{ ...styles.entityLink }}>
                  {deal.working_data?.funding_entity_snapshot?.display_name || deal.working_data?.funding_entity_snapshot?.legal_name || '—'}
                </Link>
              ) : (
                '—'
              )}
            </div>
          </div>
        )}
      </div>
    );
  };


  // BUG 2 FIX: New function to render contract exports
  const downloadPDF = (exportId) => {
    // Fetch PDF with auth token and trigger download
    axios.get(
      `${API_URL}/contract-exports/${exportId}/pdf`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      }
    ).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract_export_${exportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    }).catch(err => {
      console.error('Download failed:', err);
      alert('Failed to download PDF');
    });
  };

  const renderGenerationHistory = () => {
    // Combine generation logs and contract exports
    const combinedHistory = [
      ...generationLogs.map(log => ({
        type: 'generation',
        id: log.id,
        documentType: log.generator_type === 'disclosure' ? '📄 Disclosure PDF' : log.generator_type === 'calculator' ? '📊 Calculator' : log.generator_type,
        date: log.created_at,
        size: log.file_size,
        status: log.status,
        output_path: log.output_path,
      })),
      ...contractExports.map(exp => ({
        type: 'contract',
        id: exp.id,
        documentType: '📋 Contract Export',
        date: exp.exported_at,
        size: null,
        status: 'success',
        exportId: exp.id,
        mainContractVersionId: exp.main_contract_version_id,
      })),
    ].sort((a, b) => new Date(b.date) - new Date(a.date));

    if (combinedHistory.length === 0) {
      return (
        <div style={{ padding: SPACING.MD, color: COLORS.TEXT_SECONDARY, ...TYPOGRAPHY.BODY_SM }}>
          No documents generated yet
        </div>
      );
    }

    const displayedItems = showAllLogs ? combinedHistory : combinedHistory.slice(0, 5);
    const hasMore = combinedHistory.length > 5;

    return (
      <div>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.historyTable}>
            <thead>
              <tr style={styles.historyHeaderRow}>
                <th style={styles.historyHeaderCell}>Document Type</th>
                <th style={styles.historyHeaderCell}>Generated</th>
                <th style={styles.historyHeaderCell}>Size</th>
                <th style={styles.historyHeaderCell}>Status</th>
                <th style={styles.historyHeaderCell}>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedItems.map((item, idx) => (
                <tr key={`${item.type}-${item.id}`} style={{...styles.historyRow, borderBottom: idx < displayedItems.length - 1 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none'}}>
                  <td style={styles.historyCell}>{item.documentType}</td>
                  <td style={styles.historyCell}>{new Date(item.date).toLocaleDateString()} {new Date(item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                  <td style={styles.historyCell}>{item.size ? `${(item.size / 1024).toFixed(1)} KB` : '—'}</td>
                  <td style={styles.historyCell}>{item.status === 'success' ? '[OK]' : '[FAIL]'}</td>
                  <td style={styles.historyCell}>
                    <div style={{ display: 'flex', gap: SPACING.SM, alignItems: 'center' }}>
                      {item.type === 'generation' && item.output_path && (
                        <a href={`${API_URL}/${item.output_path}`} target="_blank" rel="noopener noreferrer" style={{color: COLORS.PRIMARY_DARK, textDecoration: 'none', fontWeight: '600', fontSize: TYPOGRAPHY.BODY_SM.fontSize}}>
                          Download
                        </a>
                      )}
                      {item.type === 'contract' && (
                        <>
                          <button
                            onClick={() => navigate(`/deals/${id}/contract-output-editor/${item.exportId}`)}
                            style={{color: COLORS.PRIMARY_DARK, textDecoration: 'none', fontWeight: '600', fontSize: TYPOGRAPHY.BODY_SM.fontSize, background: 'none', border: 'none', cursor: 'pointer', padding: 0}}
                          >
                            Edit
                          </button>
                          <span style={{color: COLORS.BORDER_LIGHT}}>|</span>
                          <button
                            onClick={() => downloadPDF(item.exportId)}
                            style={{color: COLORS.PRIMARY_DARK, textDecoration: 'none', fontWeight: '600', fontSize: TYPOGRAPHY.BODY_SM.fontSize, background: 'none', border: 'none', cursor: 'pointer', padding: 0}}
                          >
                            Download
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div style={{ paddingTop: SPACING.MD, textAlign: 'center' }}>
            <button
              onClick={() => setShowAllLogs(!showAllLogs)}
              style={{
                background: 'none',
                border: 'none',
                color: COLORS.PRIMARY_DARK,
                textDecoration: 'underline',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: TYPOGRAPHY.BODY_SM.fontSize,
              }}
            >
              {showAllLogs ? 'Show Less' : `Show All (${combinedHistory.length} total)`}
            </button>
          </div>
        )}
      </div>
    );
  };


  return (
    <div style={styles.container}>
      {error && (
        <div style={{
          padding: SPACING.LG,
          backgroundColor: '#fee2e2',
          borderRadius: RADIUS.DEFAULT,
          color: COLORS.ERROR,
          marginBottom: SPACING.XL,
        }}>
          {error}
        </div>
      )}

      {/* Deal Summary Card */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>Deal Summary</h3>
        </div>
        {renderDealSummary(wd)}
      </div>

      {/* Financial Terms Card */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>Financial Terms</h3>
        </div>
        <div style={styles.gridTwoCols}>
          <div style={styles.infoRow}>
            <div style={styles.label}>Purchase Price</div>
            <div style={styles.value}>
              ${parseFloat(getWdField(wd, 'purchase_price') || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
            </div>
          </div>
          <div style={styles.infoRow}>
            <div style={styles.label}>Revenue Purchased</div>
            <div style={styles.value}>
              ${parseFloat(getWdField(wd, 'revenue_purchased') || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
            </div>
          </div>
          <div style={styles.infoRow}>
            <div style={styles.label}>Factor Rate</div>
            <div style={styles.value}>
              {parseFloat(getWdField(wd, 'factor_rate') || 0).toFixed(3)}
            </div>
          </div>
          <div style={styles.infoRow}>
            <div style={styles.label}>APR</div>
            <div style={styles.value}>
              {wd?.calculated_outputs?.apr ? parseFloat(wd.calculated_outputs.apr).toFixed(2) + '%' : '—'}
            </div>
          </div>
          <div style={styles.infoRow}>
            <div style={styles.label}>Daily Payback</div>
            <div style={styles.value}>
              ${parseFloat(getWdField(wd, 'daily_payback') || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
            </div>
          </div>
          <div style={styles.infoRow}>
            <div style={styles.label}>Payback Months</div>
            <div style={styles.value}>
              {getWdField(wd, 'payback_months') || '—'}
            </div>
          </div>
          {wd?.calculated_outputs?.payment_amount && (
            <div style={styles.infoRow}>
              <div style={styles.label}>Payment Amount</div>
              <div style={styles.value}>
                ${parseFloat(wd.calculated_outputs.payment_amount).toLocaleString('en-US', { maximumFractionDigits: 2 })}
              </div>
            </div>
          )}
          {wd?.deal_terms?.num_payment_weeks && (
            <div style={styles.infoRow}>
              <div style={styles.label}>Payment Weeks</div>
              <div style={styles.value}>
                {wd.deal_terms.num_payment_weeks}
              </div>
            </div>
          )}
          {wd?.deal_terms?.payment_frequency && (
            <div style={styles.infoRow}>
              <div style={styles.label}>Payment Frequency</div>
              <div style={styles.value}>
                {wd.deal_terms.payment_frequency}
              </div>
            </div>
          )}
          <div style={styles.infoRow}>
            <div style={styles.label}>Submitter</div>
            <div style={styles.value}>
              {getWdField(wd, 'submitter_name') || '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Generation History Card */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>Generation History</h3>
        </div>
        {renderGenerationHistory()}
      </div>

    </div>
  );
}

const styles = {
  container: {
    padding: `${SPACING.MD} ${SPACING.XL} ${SPACING.XXL} ${SPACING.XL}`,
    maxWidth: '1400px',
    margin: '0 auto',
  },

  card: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    padding: SPACING.LG,
    marginBottom: SPACING.XXXL,
    boxShadow: SHADOWS.DEFAULT,
  },

  cardHeader: {
    marginBottom: SPACING.XL,
    paddingBottom: SPACING.LG,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    marginLeft: `-${SPACING.LG}`,
    marginRight: `-${SPACING.LG}`,
    marginTop: `-${SPACING.LG}`,
    paddingLeft: SPACING.LG,
    paddingRight: SPACING.LG,
    paddingTop: SPACING.LG,
    borderRadius: `${RADIUS.DEFAULT} ${RADIUS.DEFAULT} 0 0`,
  },

  cardTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  entityLink: {
    color: COLORS.LINK,
    textDecoration: 'none',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  gridTwoCols: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: SPACING.XL,
  },

  infoRow: {
    padding: SPACING.MD,
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.XS,
  },

  label: {
    ...TYPOGRAPHY.LABEL,
    color: COLORS.TEXT_SECONDARY,
  },

  value: {
    ...TYPOGRAPHY.BODY,
    color: COLORS.TEXT_PRIMARY,
    fontWeight: '600',
  },

  historyTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  historyHeaderRow: {
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },

  historyHeaderCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    textAlign: 'left',
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    color: COLORS.TEXT_PRIMARY,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  historyRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },

  historyCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    color: COLORS.TEXT_PRIMARY,
    verticalAlign: 'middle',
  },
};
